import type { ISpeakerProfile, IStreamingSegment } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Group, Stack, Title } from "@mantine/core"
import { useState } from "react"
import { useSpeakerHypothesisActions } from "../../hooks/useSpeakerHypothesisActions"
import { coerceSpeakerQuotes, normalizeAnalysis, speakerLabelDisplayMap } from "../../lib/speaker-mapping"
import { buildMergeOptions, type ISpeakerVM, speakerDisplayLabel } from "../../lib/speaker-vm"
import { NeedsNamesPill } from "../SpeakerChip/NeedsNamesPill"
import { SpeakerStatusBadges } from "../SpeakerChip/SpeakerStatusBadges"
import { SpeakerEditModal } from "../SpeakerEditModal"
import {
  buildEvidenceLine,
  conflictDisplayLabel,
  conflictWithLabelFor,
  sampleDurationSeconds,
} from "../WhoSpokePanel/helpers"
import { SpeakerRow } from "../WhoSpokePanel/SpeakerRow"
import type { IProps } from "./interfaces"

/**
 * Reconstruct the saved profile the edit card pre-fills from, from the VM's
 * profile-derived fields. Returns undefined when the speaker carries no profile
 * signal at all — matching the old `profileMap.get(name)` miss, so the card
 * opens in its "no saved profile" state rather than a null-filled one.
 */
function profileFromVM(vm: ISpeakerVM): ISpeakerProfile | undefined {
  if (vm.color === null && vm.title === null && vm.speakerId === null && vm.recognitionSource === null) {
    return undefined
  }
  return {
    name: vm.displayName,
    color: vm.color,
    title: vm.title,
    speaker_id: vm.speakerId,
    recognition_source: vm.recognitionSource,
  }
}

/**
 * The meeting's speaker roster: one chip per speaker (recognition icon +
 * pencil + suggestion affordance) plus the single `SpeakerEditModal`. Smart —
 * it owns the shared confirm/reject hypothesis-action hook so the suggestion ✓/✗
 * on each roster chip cross-invalidates every speaker surface on success.
 */
export function SpeakerPanel({
  meetingId,
  speakers,
  analysis,
  onRenamed,
  segments,
  audioUrl,
  onListen,
}: IProps): React.JSX.Element | null {
  // 1. STATE
  const [selected, setSelected] = useState<ISpeakerVM | null>(null)

  // 2. EXTERNAL HOOKS — the ONE confirm/reject owner (cross-invalidates on success).
  const { i18n } = useLingui()
  const hypothesis = useSpeakerHypothesisActions(meetingId)

  // Representative quotes per speaker (keyed on the resolved display name).
  const quotesMap = new Map<string, string[]>()
  for (const sq of coerceSpeakerQuotes(analysis?.speaker_quotes)) {
    quotesMap.set(sq.speaker, sq.quotes)
  }

  // Auto-detected role/title from analysis contributions. The raw analysis may
  // still carry "Name (Speaker_N)" in the name field — normalize against the
  // resolved roster first so the keys match the display names.
  const autoTitleMap = new Map<string, string>()
  if (analysis?.attendee_contributions) {
    const normalizedContributions = normalizeAnalysis(
      { attendee_contributions: analysis.attendee_contributions },
      speakerLabelDisplayMap(speakers),
    ).attendee_contributions
    if (Array.isArray(normalizedContributions)) {
      for (const ac of normalizedContributions) {
        if (ac !== null && typeof ac === "object" && "name" in ac && "role" in ac) {
          const name = ac.name
          const role = ac.role
          if (typeof name === "string" && typeof role === "string" && role.length > 0) autoTitleMap.set(name, role)
        }
      }
    }
  }

  // Per-speaker segments for voice-sample playback, keyed on the raw diarisation
  // label (segments never carry the resolved name).
  const speakerSegmentsByLabel = new Map<string, IStreamingSegment[]>()
  if (segments?.length) {
    for (const seg of segments) {
      const list = speakerSegmentsByLabel.get(seg.speaker) ?? []
      list.push(seg)
      speakerSegmentsByLabel.set(seg.speaker, list)
    }
  }

  // Merging speaker A into B leaves a duplicate display name in the positional
  // roster (kept index-aligned with the diarization labels on purpose). Dedupe
  // by display name so the panel, the count badge, and the merge-target list
  // each show B once.
  const deduped: ISpeakerVM[] = []
  const seenNames = new Set<string>()
  for (const vm of speakers) {
    if (seenNames.has(vm.displayName)) continue
    seenNames.add(vm.displayName)
    deduped.push(vm)
  }

  // When segment data is present, hide speakers with no segments at all (usually
  // transcription artifacts). Guard: segment keys can drift from resolved names
  // (legacy meetings key the VM label on the resolved name, not the raw label),
  // which would match nothing and hide EVERY speaker — so fall back to all.
  const withSegments = segments?.length
    ? deduped.filter((vm) => vm.label !== null && speakerSegmentsByLabel.has(vm.label))
    : deduped
  const visible = withSegments.length > 0 ? withSegments : deduped

  // Needs-attention speakers (unidentified + pending suggestions) sort first, so
  // the actionable slots lead the roster; the rest sort by name.
  const sorted = [...visible].sort((a, b) => {
    const aAttn = a.needsAttention ? 0 : 1
    const bAttn = b.needsAttention ? 0 : 1
    if (aAttn !== bAttn) return aAttn - bAttn
    return a.displayName.localeCompare(b.displayName)
  })

  // Speakers still needing a human decision (unidentified + pending suggestions)
  // — the "{n} need names" pill count, matching the attention-first sort above.
  const attentionCount = sorted.filter((vm) => vm.needsAttention).length

  // 6. GUARD
  if (speakers.length === 0) return null

  // 7. RENDER
  return (
    <Stack gap="xs" mb="md">
      <Group gap="sm">
        <Title order={5}>
          <Trans>Speakers</Trans>
        </Title>
        <Badge variant="light" size="sm">
          {sorted.length}
        </Badge>
        <NeedsNamesPill count={attentionCount} />
      </Group>

      <Stack gap="xs">
        {sorted.map((vm) => {
          const suggestion = vm.suggestion
          const conflictLabel = conflictWithLabelFor(vm, speakers)
          const conflictPeer = conflictLabel ? conflictDisplayLabel(conflictLabel, speakers, i18n) : null
          const window = vm.sampleWindow ?? vm.longestSegment
          const label = vm.label
          return (
            <SpeakerRow
              key={vm.label ?? vm.displayName}
              speaker={vm}
              displayLabel={speakerDisplayLabel(vm, i18n)}
              statusText={
                vm.status === "suggested"
                  ? i18n._(msg`suggested`)
                  : vm.status === "unidentified"
                    ? i18n._(msg`needs a name · no suggestion`)
                    : vm.confirmedBy === "user"
                      ? ""
                      : vm.voiceSaved
                        ? i18n._(msg`voice saved`)
                        : ""
              }
              confirmedByYou={vm.confirmedBy === "user"}
              voiceSaved={vm.voiceSaved === true}
              evidenceLine={buildEvidenceLine(suggestion?.evidence)}
              runnerUpLine={
                suggestion?.runnerUp
                  ? i18n._(msg`Or ${suggestion.runnerUp.name} · ${Math.round(suggestion.runnerUp.score * 100)}%`)
                  : null
              }
              conflictLine={
                conflictPeer
                  ? i18n._(msg`Same voice as ${conflictPeer} in two places. Listen before naming, or merge them.`)
                  : null
              }
              conflictPeerLabel={conflictPeer}
              mergeOptions={buildMergeOptions(speakers, vm.displayName)}
              sampleSeconds={sampleDurationSeconds(window)}
              showSample={Boolean(onListen && window)}
              confirmPending={Boolean(
                suggestion && label && hypothesis.pending.has(`${label}|${suggestion.name}|confirm`),
              )}
              rejectPending={Boolean(
                suggestion && label && hypothesis.pending.has(`${label}|${suggestion.name}|reject`),
              )}
              onPlaySample={window && onListen ? () => onListen(window.start) : undefined}
              onConfirm={() => {
                if (suggestion && label) void hypothesis.onAction(label, suggestion.name, "confirm")
              }}
              onReject={() => {
                if (suggestion && label) void hypothesis.onAction(label, suggestion.name, "reject")
                else setSelected(vm)
              }}
              onPickSomeoneElse={() => setSelected(vm)}
              onNameSpeaker={() => setSelected(vm)}
              onMerge={() => setSelected(vm)}
              onMergeIntoConflict={() => setSelected(vm)}
            />
          )
        })}
      </Stack>

      <SpeakerEditModal
        meetingId={meetingId}
        opened={selected !== null}
        speaker={selected}
        allSpeakers={speakers}
        quotes={selected ? (quotesMap.get(selected.displayName) ?? []) : []}
        profile={selected ? profileFromVM(selected) : undefined}
        autoColor={selected?.resolvedColor}
        autoTitle={selected ? autoTitleMap.get(selected.displayName) : undefined}
        speakerSegments={selected?.label ? speakerSegmentsByLabel.get(selected.label) : undefined}
        audioUrl={audioUrl}
        titleBadges={
          selected ? (
            <SpeakerStatusBadges status={selected.status} recognitionSource={selected.recognitionSource} />
          ) : null
        }
        onClose={() => setSelected(null)}
        onEdited={onRenamed}
      />
    </Stack>
  )
}
